import {
  RBAC_V2_ACTION_LABELS,
  RBAC_V2_FEATURE_LABELS,
  RBAC_V2_MODULE_LABELS,
  RBAC_V2_SUBTAB_LABELS,
  type RbacV2Action,
  type RbacV2Resource,
  rbacV2Resources,
} from '@gozoki/api-types'
import {
  RBAC_PERMISSION_TYPE_LABELS,
  RBAC_RESOURCE_LABELS,
} from '~/components/routes/administration/roles/resource-labels'
import type { PermissionTuple } from '~/utils/ability'

const v2ResourceSet = new Set<string>(rbacV2Resources)

export interface DescribedPermission {
  /** Clé stable pour le rendu (`action:resource`). */
  key: string
  /** Libellé de l'action (« Voir », « Modifier », action spéciale…). */
  actionLabel: string
  /** Chemin lisible de la ressource (module › sous-onglet › fonctionnalité en v2, ressource en v1). */
  segments: string[]
}

/**
 * Traduit un tuple de permission `[action, resource]` en libellés lisibles.
 * - Ressource v2 → maps `RBAC_V2_*_LABELS` de `@gozoki/api-types` (module / sous-onglet / feature).
 * - Ressource v1 → maps `RBAC_RESOURCE_LABELS` / `RBAC_PERMISSION_TYPE_LABELS`.
 */
export const describePermission = ([action, subject]: PermissionTuple): DescribedPermission => {
  // Le sujet real-estate v1 peut être un objet `{ status }` ; sinon c'est la clé de ressource.
  const resource = typeof subject === 'string' ? subject : 'real-estate'
  const key = `${String(action)}:${resource}`

  if (v2ResourceSet.has(resource)) {
    const [moduleKey, subtabKey] = resource.split('.')
    const segments = [
      RBAC_V2_MODULE_LABELS[moduleKey],
      RBAC_V2_SUBTAB_LABELS[`${moduleKey}.${subtabKey}`],
      RBAC_V2_FEATURE_LABELS[resource as RbacV2Resource],
    ].filter((segment): segment is string => Boolean(segment))

    return {
      key,
      actionLabel: RBAC_V2_ACTION_LABELS[action as RbacV2Action] ?? String(action),
      segments,
    }
  }

  return {
    key,
    actionLabel:
      RBAC_PERMISSION_TYPE_LABELS[action as keyof typeof RBAC_PERMISSION_TYPE_LABELS] ??
      String(action),
    segments: [RBAC_RESOURCE_LABELS[resource as keyof typeof RBAC_RESOURCE_LABELS] ?? resource],
  }
}
